import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, StarIcon, ArchiveBoxIcon } from '@heroicons/react/24/solid';
import { fileNotFound, notresultsfound } from '../../../assets/images/icons';
import DeleteElement from './components/DeleteElement';
import { useRefresh } from '../../../utils/RefreshProvider';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { truncateString } from '../../../utils/truncateString';
import DuplicateProd from './productCompos/DuplicateProd';

export default function ProductsList() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;
  const { refreshKey } = useRefresh();
  const [products, setProducts] = useState([]);
  const [filterProds, setFilterProds] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });

  const fetchProds = async (page = 1) => {
    let url = `${localhost}dashboard-products?page=${page}`;

    try {
      const res = await axios.get(url);
      const data = res.data.data;
      const updatedData = data.map(item => {
        const category = item.category ? JSON.parse(item.category) : null;
        const imgs = item.imgs ? JSON.parse(item.imgs) : null;
        return { ...item, category, imgs };
      });
      setProducts(updatedData);
      setPagination({
        currentPage: res.data.current_page,
        totalPages: res.data.last_page,
      });
      setFilterProds(updatedData);
    } catch (err) {
      // 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProds();
  }, [refreshKey]);

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    const filtered = products.filter((cat) =>
      cat.name[i18next.language].toLowerCase().includes(search.toLowerCase())
    );
    setFilterProds(filtered);
    setLoading(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePagination = (page) => {
    setLoading(true);
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchProds(page);
  };

  const ProductCard = ({ product }) => {
    return (
      <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-start gap-4">
          <img loading="lazy" className="h-20 w-20 rounded-md object-cover" src={product.imgs ? localimg + product.imgs[0] : fileNotFound} alt={product.name.en} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col items-start">
              <a href={`/product/${product.slug}`} className="text-sm font-medium text-gray-900 hover:underline">
                {truncateString(product.name[i18next.language], 25)}
              </a>
            </div>
            <dl className="mt-2 text-sm text-gray-500 space-y-1">
              <div className="flex justify-between">
                <dt>ID</dt>
                <dd># {product.num_product}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("price")}</dt>
                <dd className="font-medium text-gray-900">{product.price} MAD</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("stock-total")}</dt>
                <dd>{product.total_stock}</dd>
              </div>
              <div className="flex justify-between items-center">
                <dt>{t("rating")}</dt>
                <dd className="flex items-center">
                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                  {product.ratings}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("category")}</dt>
                <dd>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800"
                    data-tooltip-id="my-tooltip" data-tooltip-content={product.category[i18next.language]}>
                    {truncateString(product.category[i18next.language], 16)}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("date")}</dt>
                <dd>{formatDate(product.date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("sizes")}</dt>
                <dd>
                  <p className='pl-2'>
                    {
                      product.sizes.length > 0 ?
                        product.sizes.map((item, index) => (
                          <span key={index} className=''>
                            {item.name}{index < product.sizes.length - 1 ? ", " : ""}
                          </span>
                        ))
                        : "--"
                    }
                  </p>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>{t("colors")}</dt>
                <dd>
                  <div className='grid grid-cols-6 gap-1'>
                    {
                      product.colors.length > 0 ?
                        product.colors.map((item, index) => (
                          <div key={index} data-tooltip-id="my-tooltip" data-tooltip-content={item.name[i18next.language]}
                            className='border border-gray-300 size-5 rounded-full' style={{ background: item.hex }} />
                        ))
                        : <span className='col-span-6'>--</span>
                    }
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div >
        <div className="flex justify-end mt-2 gap-1">
          <DuplicateProd slugProd={product.slug} localhost={localhost} token={token} t={t} />
          <a href={`update-product/${product.slug}`} className="text-black transition-colors duration-150 hover:bg-black/10 rounded-md p-1.5">
            <PencilSquareIcon className="h-5 w-5" />
          </a>
          <DeleteElement elementId={product.id} typeProp="product" />
        </div>
      </div >
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("all-products")}</h1>
        <a href="/admin/create-product" className="flex items-center gap-1 text-sm bg-black hover:bg-black/80 text-white px-3 py-2 rounded-md">
          <PlusIcon className="h-5 w-5" />
          <span>Add product</span>
        </a>
      </div>

      <div className="border border-gray-200 bg-white rounded-lg shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <input className="flex-1 border border-gray-300 rounded-lg px-4 py-1.5 text-sm outline-none transition-all" placeholder={t("search-products")} type="text" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button type="submit" className="px-3 py-1.5 bg-black hover:bg-black/80 text-white rounded-lg transition-colors duration-200 flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden sm:inline">{t("search")}</span>
          </button>
        </form>
      </div>

      {loading ? (
        <div className="min-h-[55vh] flex justify-center items-center">
          <div className="loading-spinner" />
        </div>
      ) : filterProds.length > 0 ? (
        <div>
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("product")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("price")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("rating")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("category")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("sizes")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("colors")}</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t("date")}</th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t("actions")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filterProds.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center">
                        <img loading="lazy" className="h-16 w-16 rounded-md object-cover" src={product.imgs ? localimg + product.imgs[0] : fileNotFound} alt={product.name.en} />
                        <div className="ml-4">
                          <a href={`/product/${product.slug}`} className="hover:underline text-sm font-medium text-gray-900 truncate"
                            data-tooltip-id="my-tooltip" data-tooltip-content={product.name[i18next.language]}>
                            {truncateString(product.name[i18next.language], 30)}
                          </a>
                          <div className="text-sm text-gray-500">ID : {product.num_product}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-900">{product.price} MAD</div>
                      <div className="text-sm text-gray-500 capitalize">{product.gender}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-center text-start">
                        <div className="text-sm text-gray-900">{t("stock-total")} : {product.total_stock}</div>
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400" />
                          <span className="ml-1 text-sm text-gray-600">{product.ratings}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800"
                        data-tooltip-id="my-tooltip" data-tooltip-content={product.category[i18next.language]}>
                        {truncateString(product.category[i18next.language], 18)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      <p className='max-w-24'>
                        {
                          product.sizes.length > 0 ?
                            product.sizes.map((item, index) => (
                              <span key={index} className=''>
                                {item.name}{index < product.sizes.length - 1 ? ", " : ""}
                              </span>
                            ))
                            : "--"
                        }
                      </p>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      <div className='grid grid-cols-3 gap-1'>
                        {
                          product.colors.length > 0 ?
                            product.colors.map((item, index) => (
                              <div key={index} data-tooltip-id="my-tooltip" data-tooltip-content={item.name[i18next.language]}
                                className='border border-gray-300 size-6 rounded-full' style={{ background: item.hex }} />
                            ))
                            : "--"
                        }
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {formatDate(product.date)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <DuplicateProd slugProd={product.slug} localhost={localhost} token={token} t={t} />
                        <a data-tooltip-id="my-tooltip" data-tooltip-content={t("stock-management")} href={"/admin/stock-management/" + product.slug} className='text-black transition-colors duration-150 hover:bg-black/10 rounded-md p-1.5'>
                          <ArchiveBoxIcon className='size-5' />
                        </a>
                        <a href={`update-product/${product.slug}`} className="text-black transition-colors duration-150 hover:bg-black/10 rounded-md p-1.5">
                          <PencilSquareIcon className="h-5 w-5" />
                        </a>
                        <DeleteElement elementId={product.id} typeProp="product" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-2">
            {filterProds.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-4 my-8">
            <button
              onClick={() => handlePagination(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="inline-flex items-center text-sm font-medium transition-colors rounded-sm
              disabled:opacity-30 disabled:cursor-not-allowed
              bg-white text-black/80
              hover:bg-gray-50 hover:text-black
              focus:outline-none focus:ring-1 focus:ring-orange-500 focus:ring-offset-2"
            >
              <ChevronLeftIcon className="size-6" />
            </button>

            <div className="flex items-center text-sm font-medium text-black/80">
              <span className="px-3 py-1 rounded-lg bg-black/5">
                {pagination.currentPage}
              </span>
              <span className="mx-2">{t("of")}</span>
              <span className="px-3 py-1 rounded-lg bg-black/5">
                {pagination.totalPages}
              </span>
            </div>

            <button
              onClick={() => handlePagination(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="inline-flex items-center text-sm font-medium transition-colors rounded-sm
              disabled:opacity-30 disabled:cursor-not-allowed
              bg-white text-black/80
              hover:bg-gray-50 hover:text-black
              focus:outline-none focus:ring-1 focus:ring-orange-500 focus:ring-offset-2"
            >
              <ChevronRightIcon className="size-6" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <img loading="lazy" src={notresultsfound} className="w-40 h-auto mb-4" alt="No results found" />
        </div>
      )}
    </div>
  );
}